import {
  Card,
  Grid,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
} from '@mui/material';
import DashboardNavbar from 'components/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import { useEffect, useState } from 'react';
import { getGiveawayLeaderboard } from 'services/giveaway/get-leaderboard';
import { getGiveawayInfo } from 'services/giveaway/get-info';

export default function Giveaway() {
  const [loading, setLoading] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [giveawayInfo, setGiveawayInfo] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [leaderboardRes, infoRes] = await Promise.all([
        getGiveawayLeaderboard({ limit: 100 }),
        getGiveawayInfo(),
      ]);

      if (leaderboardRes?.data) {
        setLeaderboardData(leaderboardRes.data);
      } else if (leaderboardRes?.response) {
        setError(leaderboardRes.response?.data?.message || 'Failed to fetch leaderboard');
      }

      if (infoRes?.data) {
        setGiveawayInfo(infoRes.data);
      } else if (infoRes?.response && !leaderboardRes?.response) {
        setError(infoRes.response?.data?.message || 'Failed to fetch giveaway info');
      }
    } catch (err) {
      console.error('Error fetching giveaway data:', err);
      setError(err?.response?.data?.message || 'Error fetching giveaway data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRankColor = (rank) => {
    if (rank === 1) return '#FFD700';
    if (rank === 2) return '#C0C0C0';
    if (rank === 3) return '#CD7F32';
    return 'inherit';
  };

  const getRankBackgroundColor = (rank) => {
    if (rank === 1) return 'rgba(255, 215, 0, 0.15)';
    if (rank === 2) return 'rgba(192, 192, 192, 0.15)';
    if (rank === 3) return 'rgba(205, 127, 50, 0.15)';
    return null;
  };

  const getRankLabel = (rank) => {
    if (rank === 1) return '1st';
    if (rank === 2) return '2nd';
    if (rank === 3) return '3rd';
    return `#${rank}`;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <MDTypography variant='h4' fontWeight='bold' color='dark'>
              Giveaway Dashboard
            </MDTypography>
            <Button
              variant='outlined'
              color='primary'
              onClick={fetchData}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.75,
                borderRadius: 1,
                color: 'dark.main',
                borderColor: 'dark.main',
                '&:hover': {
                  borderColor: 'dark.main',
                  backgroundColor: 'grey.100',
                },
              }}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </MDBox>
        </MDBox>

        {error && (
          <MDBox mb={2}>
            <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, bgcolor: 'error.lighter' }}>
              <MDTypography variant='body2' color='error'>
                {error}
              </MDTypography>
            </Card>
          </MDBox>
        )}

        {loading ? (
          <MDBox py={4} textAlign='center'>
            <CircularProgress size={32} />
            <MDTypography variant='body2' color='text' mt={1}>
              Loading giveaway data...
            </MDTypography>
          </MDBox>
        ) : (
          <Grid container spacing={2}>
            {/* Giveaway Info Cards */}
            {giveawayInfo && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                    <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                      Status
                    </MDTypography>
                    <MDBox display='flex' alignItems='center' gap={1}>
                      <Chip
                        label={giveawayInfo.isActive ? 'ACTIVE' : 'INACTIVE'}
                        color={giveawayInfo.isActive ? 'success' : 'default'}
                        size='small'
                        sx={{ fontWeight: 'bold' }}
                      />
                    </MDBox>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                    <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                      Days Remaining
                    </MDTypography>
                    <MDTypography variant='h4' fontWeight='bold' color='primary'>
                      {giveawayInfo.daysRemaining || 0}
                    </MDTypography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                    <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                      Total Participants
                    </MDTypography>
                    <MDTypography variant='h4' fontWeight='bold' color='success'>
                      {giveawayInfo.totalParticipants?.toLocaleString() || 0}
                    </MDTypography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                    <MDTypography variant='caption' fontWeight='medium' color='textSecondary'>
                      Total Tickets
                    </MDTypography>
                    <MDTypography variant='h4' fontWeight='bold' color='info'>
                      {giveawayInfo.totalTickets?.toLocaleString() || 0}
                    </MDTypography>
                  </Card>
                </Grid>

                {/* Prize Pool Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                    <MDTypography variant='subtitle2' fontWeight='bold' color='dark' mb={1}>
                      Prize Pool
                    </MDTypography>
                    <MDTypography variant='h3' fontWeight='bold' color='warning' mb={2}>
                      ${giveawayInfo.totalPrizePool?.toLocaleString() || 0} USDT
                    </MDTypography>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <MDBox
                          display='flex'
                          justifyContent='space-between'
                          alignItems='center'
                          py={0.5}
                          px={1}
                          sx={{ backgroundColor: 'rgba(255, 215, 0, 0.1)', borderRadius: 1 }}
                        >
                          <MDTypography variant='body2' fontWeight='bold' sx={{ color: '#FFD700' }}>
                            1st Place
                          </MDTypography>
                          <MDTypography variant='body2' fontWeight='bold'>
                            ${giveawayInfo.prizes?.first?.winner?.toLocaleString() || 0} (+ ${giveawayInfo.prizes?.first?.referrer?.toLocaleString() || 0} referrer)
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox
                          display='flex'
                          justifyContent='space-between'
                          alignItems='center'
                          py={0.5}
                          px={1}
                          sx={{ backgroundColor: 'rgba(192, 192, 192, 0.1)', borderRadius: 1 }}
                        >
                          <MDTypography variant='body2' fontWeight='bold' sx={{ color: '#C0C0C0' }}>
                            2nd Place
                          </MDTypography>
                          <MDTypography variant='body2' fontWeight='bold'>
                            ${giveawayInfo.prizes?.second?.winner?.toLocaleString() || 0} (+ ${giveawayInfo.prizes?.second?.referrer?.toLocaleString() || 0} referrer)
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox
                          display='flex'
                          justifyContent='space-between'
                          alignItems='center'
                          py={0.5}
                          px={1}
                          sx={{ backgroundColor: 'rgba(205, 127, 50, 0.1)', borderRadius: 1 }}
                        >
                          <MDTypography variant='body2' fontWeight='bold' sx={{ color: '#CD7F32' }}>
                            3rd Place
                          </MDTypography>
                          <MDTypography variant='body2' fontWeight='bold'>
                            ${giveawayInfo.prizes?.third?.winner?.toLocaleString() || 0} (+ ${giveawayInfo.prizes?.third?.referrer?.toLocaleString() || 0} referrer)
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                {/* Giveaway Period Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, height: '100%' }}>
                    <MDTypography variant='subtitle2' fontWeight='bold' color='dark' mb={1}>
                      Giveaway Period
                    </MDTypography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <MDTypography variant='caption' color='textSecondary'>
                          Start Date
                        </MDTypography>
                        <MDTypography variant='body2' fontWeight='medium'>
                          {formatDate(giveawayInfo.startDate)}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={6}>
                        <MDTypography variant='caption' color='textSecondary'>
                          End Date
                        </MDTypography>
                        <MDTypography variant='body2' fontWeight='medium'>
                          {formatDate(giveawayInfo.endDate)}
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </>
            )}

            {/* Leaderboard Table */}
            <Grid item xs={12}>
              <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2 }}>
                <MDTypography variant='subtitle2' fontWeight='bold' color='dark' mb={1}>
                  Ticket Leaderboard (Top 100)
                </MDTypography>

                {!leaderboardData?.leaderboard || leaderboardData.leaderboard.length === 0 ? (
                  <MDTypography variant='caption' color='textSecondary'>
                    No participants yet
                  </MDTypography>
                ) : (
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size='small' stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 1, backgroundColor: 'grey.100', width: 60 }}>
                            <MDTypography variant='caption' fontWeight='bold'>
                              Rank
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ py: 1, backgroundColor: 'grey.100' }}>
                            <MDTypography variant='caption' fontWeight='bold'>
                              Player
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={{ py: 1, backgroundColor: 'grey.100' }}>
                            <MDTypography variant='caption' fontWeight='bold'>
                              User ID
                            </MDTypography>
                          </TableCell>
                          <TableCell align='right' sx={{ py: 1, backgroundColor: 'grey.100' }}>
                            <MDTypography variant='caption' fontWeight='bold'>
                              Tickets
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leaderboardData.leaderboard.map((entry, index) => (
                          <TableRow
                            key={entry.oddsUserId}
                            sx={{
                              backgroundColor: getRankBackgroundColor(entry.rank) || 
                                (index % 2 === 0 ? 'white' : 'grey.25'),
                            }}
                          >
                            <TableCell sx={{ py: 0.75 }}>
                              <MDTypography
                                variant='body2'
                                fontWeight={entry.rank <= 3 ? 'bold' : 'medium'}
                                sx={{ color: getRankColor(entry.rank) }}
                              >
                                {getRankLabel(entry.rank)}
                              </MDTypography>
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <MDBox display='flex' alignItems='center' gap={1}>
                                <Avatar
                                  src={entry.photoUrl}
                                  alt={entry.nickname}
                                  sx={{ width: 28, height: 28 }}
                                >
                                  {entry.nickname?.charAt(0)?.toUpperCase() || '?'}
                                </Avatar>
                                <MDTypography
                                  variant='body2'
                                  fontWeight={entry.rank <= 3 ? 'bold' : 'medium'}
                                >
                                  {entry.nickname || 'Anonymous'}
                                </MDTypography>
                              </MDBox>
                            </TableCell>
                            <TableCell sx={{ py: 0.75 }}>
                              <MDTypography variant='caption' color='textSecondary'>
                                {entry.oddsUserId?.slice(0, 8)}...
                              </MDTypography>
                            </TableCell>
                            <TableCell align='right' sx={{ py: 0.75 }}>
                              <Chip
                                label={entry.tickets}
                                size='small'
                                color={entry.rank <= 3 ? 'primary' : 'default'}
                                sx={{
                                  fontWeight: 'bold',
                                  minWidth: 40,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
